import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PublicLayout from "../shell/PublicLayout";
import Overline from "../components/Overline";
import { paths } from "../routing/paths";

/*
 * /privacy-policy in the redesign — the same agreement text as the legacy
 * page (it's a legal document; the words stay verbatim), reset in the
 * shared page grammar: breadcrumb, title block, one white prose card with
 * real headings instead of underlined divs.
 */

function PolicyHeading({ children }) {
  return (
    <h2 className="m-0 mt-2 text-[20px] font-bold leading-7 tracking-[-0.2px] text-black">{children}</h2>
  );
}

const P = ({ children }) => <p className="m-0 text-[16px] leading-7 text-ic-body">{children}</p>;

export default function PrivacyPolicyPage() {
  return (
    <PublicLayout>
      <Helmet>
        <title>Privacy policy | Inside Chassidus</title>
      </Helmet>
      <main className="max-w-[960px] w-full mx-auto px-5 pt-5 pb-10 flex flex-col gap-5">
        <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-[14px] leading-5 whitespace-nowrap">
          <Link to={paths.home()} className="text-ic-blue font-semibold py-3 px-1 -my-3 inline-flex items-center min-h-[44px] hover:underline">
            Home
          </Link>
          <span className="text-ic-slate">→</span>
          <span className="text-ic-body font-semibold px-1">Privacy policy</span>
        </nav>

        <section className="flex flex-col gap-2">
          <Overline>Legal</Overline>
          <h1 className="m-0 font-extrabold text-black tracking-[-0.5px]" style={{ fontSize: "clamp(26px, 6vw, 40px)", lineHeight: 1.2 }}>
            Online Privacy Policy Agreement
          </h1>
        </section>

        <section className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised flex flex-col gap-4 max-w-[840px]" style={{ padding: "clamp(20px, 5vw, 32px)" }}>
          <P>
            Inside Chassidus is committed to keeping any and all personal information collected of those
            individuals that visit our website and make use of our online facilities and services accurate,
            confidential, secure and private. Our privacy policy has been designed and created to ensure those
            affiliated with Inside Chassidus of our commitment and realization of our obligation not only to
            meet but to exceed most existing privacy standards.
          </P>
          <P>
            THEREFORE, this Privacy Policy Agreement shall apply to Inside Chassidus, and thus it shall govern
            any and all data collection and usage thereof. Through the use of Insidechassidus.org you are
            herein consenting to the following data procedures expressed within this agreement.
          </P>

          <PolicyHeading>Collection of information</PolicyHeading>
          <P>This website collects various types of information, such as:</P>
          <ul className="m-0 pl-5 flex flex-col gap-2 list-disc text-[16px] leading-7 text-ic-body">
            <li>
              Voluntarily provided information which may include your name, address, email address, billing
              and/or credit card information etc., which may be used when you purchase products and/or services
              and to deliver the services you have requested.
            </li>
            <li>
              Information automatically collected when visiting our website, which may include cookies, third
              party tracking technologies and server logs.
            </li>
          </ul>
          <P>
            Please rest assured that this site shall only collect personal information that you knowingly and
            willingly provide by way of surveys, completed membership forms, and emails. It is the intent of
            this site to use personal information only for the purpose for which it was requested and any
            additional uses specifically provided on this site.
          </P>
          <P>
            We may also gather information about the type of browser you are using, IP address or type of
            operating system to assist us in providing and maintaining superior quality service.
          </P>
          <P>
            It is highly recommended and suggested that you review the privacy policies and statements of any
            website you choose to use or frequent as a means to better understand the way in which other
            websites garner, make use of and share information collected.
          </P>

          <PolicyHeading>Use of information collected</PolicyHeading>
          <P>
            Inside Chassidus may collect and may make use of personal information to assist in the operation
            of our website and to ensure delivery of the services you need and request. At times, we may find
            it necessary to use personally identifiable information as a means to keep you informed of other
            possible products and/or services that may be available to you from Insidechassidus.org. Inside
            Chassidus may also be in contact with you with regards to completing surveys and/or research
            questionnaires related to your opinion of current or potential future services that may be offered.
          </P>
          <P>
            Inside Chassidus does not now, nor will it in the future, sell, rent or lease any of our customer
            lists and/or names to any third parties.
          </P>
          <P>
            Inside Chassidus may deem it necessary to follow websites and/or pages that our users may frequent
            in an effort to gleam what types of services and/or products may be the most popular to customers
            or the general public.
          </P>
          <P>
            Inside Chassidus may disclose your personal information, without prior notice to you, only if
            required to do so in accordance with applicable laws and/or in a good faith belief that such action
            is deemed necessary or is required in an effort to:
          </P>
          <ul className="m-0 pl-5 flex flex-col gap-2 list-disc text-[16px] leading-7 text-ic-body">
            <li>
              Remain in conformance with any decrees, laws and/or statutes or in an effort to comply with any
              process which may be served upon Inside Chassidus and/or our website.
            </li>
            <li>Maintain, safeguard and/or preserve all the rights and/or property of Inside Chassidus.</li>
            <li>
              Perform under demanding conditions in an effort to safeguard the personal safety of users of
              Insidechassidus.org and/or the general public.
            </li>
          </ul>

          <PolicyHeading>Children under age of 13</PolicyHeading>
          <P>
            Inside Chassidus does not knowingly collect personal identifiable information from children under
            the age of thirteen (13) without verifiable parental consent. If it is determined that such
            information has been inadvertently collected on anyone under the age of thirteen (13), we shall
            immediately take the necessary steps to ensure that such information is deleted from our system's
            database. Anyone under the age of thirteen (13) must seek and obtain parent or guardian permission
            to use this website.
          </P>

          <PolicyHeading>Unsubscribe or opt-out</PolicyHeading>
          <P>
            All users and/or visitors to our website have the option to discontinue receiving communication
            from us and/or reserve the right to discontinue receiving communications by way of email or
            newsletters. To discontinue or unsubscribe to our website please send an email that you wish to
            unsubscribe to{" "}
            <a href="mailto:Dlpaltiel@insidechassidus.org" className="text-ic-blue font-semibold underline hover:text-ic-blueHover ic-wrap-anywhere">
              Dlpaltiel@insidechassidus.org
            </a>
            . If you wish to unsubscribe or opt-out from any third party websites, you must go to that specific
            website to unsubscribe and/or opt-out.
          </P>

          <PolicyHeading>Links to other web sites</PolicyHeading>
          <P>
            Our website does contain links to affiliate and other websites. Inside Chassidus does not claim nor
            accept responsibility for any privacy policies, practices and/or procedures of other such websites.
            Therefore, we encourage all users and visitors to be aware when they leave our website and to read
            the privacy statements of each and every website that collects personally identifiable information.
            The aforementioned Privacy Policy Agreement applies only and solely to the information collected by
            our website.
          </P>

          <PolicyHeading>Security</PolicyHeading>
          <P>
            Inside Chassidus shall endeavor and shall take every precaution to maintain adequate physical,
            procedural and technical security with respect to our offices and information storage facilities so
            as to prevent any loss, misuse, unauthorized access, disclosure or modification of the user's
            personal information under our control.
          </P>

          <PolicyHeading>Changes to Privacy Policy Agreement</PolicyHeading>
          <P>
            Inside Chassidus reserves the right to update and/or change the terms of our privacy policy, and as
            such we will post those change to our website homepage at Insidechassidus.org, so that our users
            and/or visitors are always aware of the type of information we collect, how it will be used, and
            under what circumstances, if any, we may disclose such information. If at any point in time Inside
            Chassidus decides to make use of any personally identifiable information on file, in a manner
            vastly different from that which was stated when this information was initially collected, the user
            or users shall be promptly notified by email. Users at that time shall have the option as to
            whether or not to permit the use of their information in this separate manner.
          </P>

          <PolicyHeading>Acceptance of terms</PolicyHeading>
          <P>
            Through the use of this website, you are hereby accepting the terms and conditions stipulated
            within the aforementioned Privacy Policy Agreement. If you are not in agreement with our terms and
            conditions, then you should refrain from further use of our sites. In addition, your continued use
            of our website following the posting of any updates or changes to our terms and conditions shall
            mean that you are in agreement and acceptance of such changes.
          </P>

          <PolicyHeading>How to contact us</PolicyHeading>
          <P>
            If you have any questions or concerns regarding the Privacy Policy Agreement related to our
            website, please feel free to reach us through the{" "}
            <Link to={paths.contact()} className="text-ic-blue font-semibold underline hover:text-ic-blueHover">
              contact page
            </Link>{" "}
            or by email at{" "}
            <a href="mailto:Info@insidechassidus.org" className="text-ic-blue font-semibold underline hover:text-ic-blueHover ic-wrap-anywhere">
              Info@insidechassidus.org
            </a>
            .
          </P>
        </section>
      </main>
    </PublicLayout>
  );
}
